import type { FunctionalComponent } from 'preact';
import { useState } from 'preact/hooks';
import type { JSX } from 'preact/jsx-runtime';

import type { t_BottomSheetProps } from '@/types/BottomSheet';

export const BottomSheet: FunctionalComponent<t_BottomSheetProps> = ({ disclosure, children }) => {
  const { isOpen, onClose } = disclosure;

  const [startY, setStartY] = useState<number | null>(null);
  const [translateY, setTranslateY] = useState(0);

  if (!isOpen) return null;

  const handleTouchStart = (e: JSX.TargetedTouchEvent<HTMLDivElement>) => {
    const touch = e.touches[0];
    if (!touch) return;
    setStartY(touch.clientY);
  };

  const handleTouchMove = (e: JSX.TargetedTouchEvent<HTMLDivElement>) => {
    if (startY === null) return;
    const touch = e.touches[0];
    if (!touch) return;
    const current = touch.clientY;
    const diff = current - startY;

    if (diff > 0) setTranslateY(diff); // drag downward only
  };

  const handleTouchEnd = () => {
    if (translateY > 80) onClose();
    setTranslateY(0);
    setStartY(null);
  };

  const handleMouseDown = (e: JSX.TargetedMouseEvent<HTMLDivElement>) => {
    setStartY(e.clientY);
  };

  const handleMouseMove = (e: JSX.TargetedMouseEvent<HTMLDivElement>) => {
    if (startY === null) return;
    const diff = e.clientY - startY;
    if (diff > 0) setTranslateY(diff);
  };

  const handleMouseUp = () => {
    if (translateY > 80) onClose();
    setTranslateY(0);
    setStartY(null);
  };

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 40,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        pointerEvents: 'auto',
        transition: 'background 0.3s ease',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(240, 240, 240, 0.45)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          opacity: translateY > 0 ? Math.max(0, 1 - translateY / 300) : 1,
          transition: 'opacity 0.2s ease',
        }}
        onClick={onClose}
      />

      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '480px',
          background: '#fff',
          borderRadius: '16px 16px 0 0',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          transform: `translateY(${translateY}px)`,
          transition: startY ? 'none' : 'transform 0.25s ease-out',
          touchAction: 'none',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={startY !== null ? handleMouseMove : undefined}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div style={{ padding: '24px' }}>{children}</div>
      </div>
    </div>
  );
};
